import { type JSX, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbar,
  type GridColDef,
} from '@mui/x-data-grid';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LaunchIcon from '@mui/icons-material/Launch';
import QRCode from 'qrcode';

import { deleteDevice, listDevices, type PortalDevice, updateDevice } from '../../api.js';
import { confirmDialog, promptDialog } from '../../components/dialogs.js';

function nicknameOf(d: PortalDevice): string {
  const n = d.metadata && typeof d.metadata['nickname'] === 'string' ? d.metadata['nickname'] : null;
  return n ?? d.deviceName ?? `${d.id.slice(0, 8)}…`;
}
function statusOf(lastSeenAt: number): 'online' | 'idle' | 'offline' {
  const age = Math.floor(Date.now() / 1000) - lastSeenAt;
  return age < 15 * 60 ? 'online' : age < 24 * 3600 ? 'idle' : 'offline';
}
function relTime(sec: number): string {
  const s = Math.floor(Date.now() / 1000) - sec;
  if (s < 60) return `${String(s)}s ago`;
  if (s < 3600) return `${String(Math.round(s / 60))}m ago`;
  if (s < 86400) return `${String(Math.round(s / 3600))}h ago`;
  return new Date(sec * 1000).toLocaleDateString();
}
const STATUS_COLOR: Record<'online' | 'idle' | 'offline', 'success' | 'warning' | 'default'> = {
  online: 'success',
  idle: 'warning',
  offline: 'default',
};

/** Device roster (MUI DataGrid) + rename/delete per device + QR enrollment. */
export function DevicesSection({
  tenantId,
  editToken,
  launcherUrl,
}: {
  tenantId: string;
  editToken: string;
  launcherUrl: string;
}): JSX.Element {
  const [devices, setDevices] = useState<PortalDevice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [qr, setQr] = useState<string>('');

  async function load(): Promise<void> {
    const res = await listDevices({ tenantId, editToken });
    if (res.kind === 'ok') {
      setDevices(res.devices);
      setError(null);
    } else {
      setError(res.kind === 'unauthorized' ? 'Edit token rejected.' : res.kind === 'network' ? res.message : `Server returned ${String(res.status)}.`);
    }
  }
  useEffect(() => {
    setDevices(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    if (!enrollOpen) return;
    void QRCode.toDataURL(launcherUrl, { width: 240, margin: 1 }).then(setQr, () => setQr(''));
  }, [enrollOpen, launcherUrl]);

  async function rename(d: PortalDevice): Promise<void> {
    const next = await promptDialog({ title: 'Rename device', message: 'Nickname (blank clears it):', defaultValue: nicknameOf(d) });
    if (next === null) return;
    const res = await updateDevice({ tenantId, editToken, deviceId: d.id, nickname: next.trim() === '' ? null : next.trim() });
    if (res.kind === 'ok') void load();
    else setError('Could not rename the device.');
  }
  async function remove(d: PortalDevice): Promise<void> {
    const ok = await confirmDialog({
      title: 'Delete device',
      message: `Delete “${nicknameOf(d)}”? It re-registers if the unit boots into the launcher again.`,
      destructive: true,
      confirmLabel: 'Delete',
      requireText: 'delete',
    });
    if (!ok) return;
    const res = await deleteDevice({ tenantId, editToken, deviceId: d.id });
    if (res.kind === 'ok') void load();
    else setError('Could not delete the device.');
  }

  const columns: GridColDef<PortalDevice>[] = [
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      valueGetter: (_v, r) => statusOf(r.lastSeenAt),
      renderCell: (p) => <Chip size="small" label={String(p.value)} color={STATUS_COLOR[p.value as 'online' | 'idle' | 'offline']} />,
    },
    { field: 'device', headerName: 'Device', flex: 1, minWidth: 160, valueGetter: (_v, r) => nicknameOf(r) },
    { field: 'os', headerName: 'Android', width: 120, valueGetter: (_v, r) => r.osVersion ?? '—' },
    { field: 'location', headerName: 'Location', width: 150, valueGetter: (_v, r) => [r.city, r.country].filter(Boolean).join(', ') || '—' },
    { field: 'ip', headerName: 'IP', width: 140, valueGetter: (_v, r) => r.localIpv4 ?? r.ipAddress ?? '—' },
    { field: 'bootCount', headerName: 'Boots', width: 80, type: 'number' },
    { field: 'lastSeen', headerName: 'Last seen', width: 120, valueGetter: (_v, r) => relTime(r.lastSeenAt) },
    {
      field: 'actions',
      type: 'actions',
      width: 90,
      getActions: (p) => [
        <GridActionsCellItem key="rename" icon={<EditIcon />} label="Rename" onClick={() => void rename(p.row)} />,
        <GridActionsCellItem key="delete" icon={<DeleteOutlineIcon />} label="Delete" onClick={() => void remove(p.row)} />,
      ],
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="h5" fontWeight={700} sx={{ flexGrow: 1 }}>Devices</Typography>
        <Button variant="contained" startIcon={<QrCode2Icon />} onClick={() => setEnrollOpen(true)} data-testid="enroll-device">
          Enroll a device
        </Button>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Every kiosk that has booted into this fleet’s launcher. Rename, remove, or onboard a new one.
      </Typography>

      {error !== null && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ flexGrow: 1, minHeight: 360, minWidth: 0 }}>
        <DataGrid
          rows={devices ?? []}
          columns={columns}
          loading={devices === null}
          density="compact"
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true, csvOptions: { fileName: `mobilauncher-${tenantId}-devices` } } }}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
        />
      </Box>

      <Dialog open={enrollOpen} onClose={() => setEnrollOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Enroll a device</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
            Point a kiosk-capable MDM’s managed browser at the launcher URL, or scan to open it on a device.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            {qr ? <Box component="img" src={qr} alt="Launcher QR code" sx={{ borderRadius: 1 }} /> : <Typography color="text.secondary">Generating QR…</Typography>}
          </Box>
          <Box component="code" sx={{ display: 'block', wordBreak: 'break-all', fontSize: 13, mb: 2 }}>{launcherUrl}</Box>
          <Button fullWidth variant="outlined" startIcon={<LaunchIcon />} href={launcherUrl} target="_blank" rel="noopener">
            Open launcher
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
